import React from 'react';
import axios from 'axios';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import AlertBox from '@/components/layout/AlertBox';
import CampoUpload from '@/components/Formulario/CampoUpload';
import CampoRadio from '@/components/Formulario/CampoRadio';
import Descricao from '@/components/tipografia/Descricao';
import { Dataset, camposAtivos } from '@tera/shared/src/types/Dataset';

type Modo = 'acrescentar' | 'substituir';

type Resultado = {
    inseridos: number;
    atualizados: number;
    removidos: number;
    erros: { indice: number; erro: string }[];
    colunasIgnoradas: string[];
    camposAusentes: string[];
};

type Props = {
    dataset: Dataset;
    show: boolean;
    onClose: () => void;
    onSuccess: () => void;
};

const ResultadoImportacao = ({ resultado, modo }: { resultado: Resultado; modo: Modo }) => (
    <VBox gap={8}>
        <Descricao>
            {resultado.inseridos} inserido(s) · {resultado.atualizados} atualizado(s)
            {modo === 'substituir' ? ` · ${resultado.removidos} removido(s)` : ''}
        </Descricao>
        {!!resultado.colunasIgnoradas.length && (
            <AlertBox variant="info">
                <p>Colunas ignoradas: {resultado.colunasIgnoradas.join(', ')}</p>
            </AlertBox>
        )}
        {!!resultado.camposAusentes.length && (
            <AlertBox variant="info">
                <p>
                    Campos sem coluna no arquivo (gravados vazios):{' '}
                    {resultado.camposAusentes.join(', ')}
                </p>
            </AlertBox>
        )}
        {!!resultado.erros.length && (
            <AlertBox variant="danger">
                <p>{resultado.erros.length} linha(s) não importada(s):</p>
                <ul>
                    {resultado.erros.slice(0, 20).map((e) => (
                        <li key={e.indice}>
                            Linha {e.indice}: {e.erro}
                        </li>
                    ))}
                </ul>
            </AlertBox>
        )}
    </VBox>
);

const FormImportacao = ({
    dataset,
    file,
    onFile,
    modo,
    onModo,
    erro,
    carregando,
}: {
    dataset: Dataset;
    file: File | null;
    onFile: (f: File | null) => void;
    modo: Modo;
    onModo: (m: Modo) => void;
    erro: string | null;
    carregando: boolean;
}) => {
    const campos = camposAtivos({ campos: dataset.campos ?? [] });

    return (
        <VBox gap={12}>
            <Descricao>
                Arquivo <b>.csv</b> com cabeçalho na primeira linha. As colunas são reconhecidas
                pelo nome ou pela chave do campo: <b>{campos.map((c) => c.rotulo).join(', ')}</b>.
            </Descricao>
            <CampoUpload
                accept=".csv,.txt"
                maxFiles={1}
                value={file ? [file] : []}
                onChange={(files: File[]) => onFile(files[0] ?? null)}
                disabled={carregando}
            />
            <CampoRadio
                id="dataset-importar-acrescentar"
                name="dataset-importar-modo"
                value="acrescentar"
                checked={modo === 'acrescentar'}
                onChange={() => onModo('acrescentar')}
                disabled={!dataset.campos_chave?.length}
                label="Acrescentar/atualizar — mantém os registros que não estão no arquivo"
            />
            <CampoRadio
                id="dataset-importar-substituir"
                name="dataset-importar-modo"
                value="substituir"
                checked={modo === 'substituir'}
                onChange={() => onModo('substituir')}
                label="Substituir — remove os registros que não estão no arquivo"
            />
            {!dataset.campos_chave?.length && (
                <AlertBox variant="warning">
                    <p>
                        Esta tabela não tem chave natural, então só é possível substituir todo o
                        conteúdo. Para acrescentar/atualizar, defina a chave natural no cadastro da
                        tabela.
                    </p>
                </AlertBox>
            )}
            {modo === 'substituir' && (
                <Descricao>Se alguma linha for inválida, nada é importado nem removido.</Descricao>
            )}
            {erro && <Descricao style={{ color: 'var(--tc-color-danger-600)' }}>{erro}</Descricao>}
        </VBox>
    );
};

const ModalImportarDataset = ({ dataset, show, onClose, onSuccess }: Props) => {
    const [file, setFile] = React.useState<File | null>(null);
    const modoInicial: Modo = dataset.campos_chave?.length ? 'acrescentar' : 'substituir';
    const [modo, setModo] = React.useState<Modo>(modoInicial);
    const [resultado, setResultado] = React.useState<Resultado | null>(null);
    const [erro, setErro] = React.useState<string | null>(null);

    const handleClose = () => {
        setFile(null);
        setModo(modoInicial);
        setResultado(null);
        setErro(null);
        onClose();
    };

    const importarMutation = useMutation(
        async () => {
            const formData = new FormData();
            formData.append('file', file!);
            const res = await axios.post(`/api/dataset/${dataset.chave}/importar`, formData, {
                params: { modo },
            });
            return res.data.result as Resultado;
        },
        {
            onSuccess: (data) => {
                setErro(null);
                setResultado(data);
                onSuccess();
            },
            onError: (err: any) => {
                const msg = err.response?.data?.msg ?? 'Erro ao importar o arquivo.';
                setErro(msg);
                toast(msg, { type: 'error', position: 'top-center' });
            },
        },
    );

    return (
        <Modal
            instanceId="dataset-importar"
            show={show}
            title="Importar CSV"
            size="lg"
            onClose={handleClose}
            onCancel={handleClose}
            onConfirm={() => (resultado ? handleClose() : importarMutation.mutate())}
            confirmProps={{
                label: resultado ? 'Fechar' : 'Importar',
                variant: !resultado && modo === 'substituir' ? 'danger' : 'primary',
                disabled: !resultado && (!file || importarMutation.isLoading),
            }}
            cancelProps={resultado ? undefined : { label: 'Cancelar' }}
        >
            {resultado ? (
                <ResultadoImportacao resultado={resultado} modo={modo} />
            ) : (
                <FormImportacao
                    dataset={dataset}
                    file={file}
                    onFile={setFile}
                    modo={modo}
                    onModo={setModo}
                    erro={erro}
                    carregando={importarMutation.isLoading}
                />
            )}
        </Modal>
    );
};

export default ModalImportarDataset;
